/**
 * StatusBadge — small, isolated component that clarifies whether something
 * is actively operating, planned, in development, or conceptual.
 *
 * Usage:
 *   <StatusBadge kind="planned" />
 *   <StatusBadge kind="in-development" label="Site work begins Q3 2026" />
 *
 * Design rules (frozen UI — must remain visually consistent):
 *   - Pill-style chip, monospace tracking, gold accent
 *   - Never blocks layout — inline-flex, self-contained
 *   - Reversible: removing this component leaves no orphan styles
 */
import { Clock } from "lucide-react";

const PRESETS = {
  "planned":          { label: "Planned Development",  tone: "border-gold/40 text-gold bg-gold/5" },
  "in-development":   { label: "In Development",       tone: "border-gold/40 text-gold bg-gold/5" },
  "concept":          { label: "Concept Phase",        tone: "border-white/30 text-white/80 bg-white/5" },
  "future":           { label: "Future Initiative",    tone: "border-white/30 text-white/80 bg-white/5" },
  "coming-soon":      { label: "Coming Soon",          tone: "border-gold/40 text-gold bg-gold/5" },
  "operating":        { label: "Operating",            tone: "border-emerald-400/30 text-emerald-300 bg-emerald-400/5" },
  "in-planning":      { label: "In Planning",          tone: "border-white/30 text-white/80 bg-white/5" },
  "in-formation":     { label: "In Formation",         tone: "border-white/30 text-white/80 bg-white/5" },
};

export default function StatusBadge({ kind = "planned", label, className = "", showIcon = true }) {
  const preset = PRESETS[kind] || PRESETS.planned;
  const text = label || preset.label;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] border px-2.5 py-1 ${preset.tone} ${className}`}
      data-testid={`status-badge-${kind}`}
    >
      {showIcon && <Clock size={10} className="flex-shrink-0" />}
      {text}
    </span>
  );
}
